import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ThemeProvider, Typography, CircularProgress, Box,
} from '@material-ui/core';

import PasswordSavedPanel from './PasswordSavedPanel';

import {
  ContentContainer, FlexContainer, HeaderBox, Title, continueButtonStyles,
} from '@/pages/PreOnboarding/steps/ActivateSleepio/steps/AuthMethodSelection.styles';
import ChoosePasswordForm from '@/pages/PreOnboarding/steps/ActivateSleepio/steps/ChoosePasswordForm';
import sleepioTheme from '@/themes/sleepioTheme';
import Button from '@/components/Button';
import { SleepioHeader } from '@/pages/Onboarding/components/Header/SleepioHeader';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { logError } from '@/utils/logError';
import { FORGOT_PASSWORD_PATH, SLEEPIO_CANOPY_LOGIN_COMPLETE_PATH } from '@/utils/passwordRoutes';
import { buildRedirectParam, isSafeRedirectUri } from '@/utils/urlHelpers';
import { REDIRECT_TO_SESSION_KEY } from '@/utils/constants';

/**
 * Where a password link lands.
 *
 * **Two arrival shapes, both handled, because the estate can produce either.** The
 * mail hook builds links that go through Supabase's own verify endpoint, which
 * consumes the token and bounces the member here with a session in the URL fragment —
 * the client picks that up on its own. A template can also link straight here with
 * `?token_hash=…&type=recovery` still to be redeemed. Rather than guess which is
 * configured per environment, this reads the token when one is present and otherwise
 * waits for the fragment session to settle, the same way the magic-link callback does.
 *
 * **The hand-off code is minted only after the password saves.** It lives about thirty
 * seconds and can be used once, so asking for it any earlier would expire it while the
 * member was still typing. Nothing here touches the token; saving the password leads
 * to the existing final step, which mints it there.
 */

type Stage = 'checking' | 'ready' | 'expired' | 'saved';

/** Give the client a moment to read a session out of the URL fragment. */
const FRAGMENT_SESSION_SETTLE_MS = 1500;

const ResetPasswordInner: React.FC = () => {
  useAcceptedLanguage();

  const { t } = useTranslation();
  const history = useHistory();
  const { search } = useLocation();
  const [stage, setStage] = useState<Stage>('checking');
  const establishStarted = useRef(false);

  // Capture the app's deep link the moment we see it, and remember it.
  //
  // Two ways it can reach this screen, so both are handled. The reset email may carry
  // it on the link itself. Or it was captured earlier in the same browser session and
  // the trip out to the mail and back has since dropped it from the address bar.
  //
  // Validated before it is trusted or stored: an unchecked redirect target is an open
  // redirect, so anything that is not a known-safe app scheme is discarded.
  //
  // Every touch of session storage is guarded. A browser with storage locked down
  // throws on access, and an unguarded read in the render body would take the whole
  // reset page down with a blank screen — on the one journey a member reaches when
  // they are already locked out. Unavailable storage just means no saved deep link.
  const [effectiveRedirectTo, setEffectiveRedirectTo] = useState<string | null>(null);

  useEffect(() => {
    const fromUrl = new URLSearchParams(search).get('redirectTo');
    const safeFromUrl = fromUrl && isSafeRedirectUri(fromUrl) ? fromUrl : null;

    if (safeFromUrl) {
      setEffectiveRedirectTo(safeFromUrl);
      try {
        sessionStorage.setItem(REDIRECT_TO_SESSION_KEY, safeFromUrl);
      } catch {
        // Nothing here is worth failing a password reset over.
      }
      return;
    }

    try {
      const stored = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY);
      // Absent on a desktop reset, where there is no app to hand back to. That is the
      // ordinary case, not an error — the final step shows its own screen instead.
      if (stored && isSafeRedirectUri(stored)) { setEffectiveRedirectTo(stored); }
    } catch {
      // Same again: treat unreadable storage as "no saved deep link".
    }
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    // The scrub below changes the address bar, and this effect keys off the query
    // string. Run the body once and the two can never chase each other.
    if (establishStarted.current) { return undefined; }
    establishStarted.current = true;

    const establishSession = async () => {
      const params = new URLSearchParams(search);
      // Supabase has used both names for this over time; accept either.
      const tokenHash = params.get('token_hash') ?? params.get('token');
      const type = params.get('type');

      // Get the token out of the address bar immediately, before anything can read it.
      //
      // A recovery token is a credential: redeeming it yields a session that can set a
      // new password, so anyone holding it owns the account. Left in the URL it does
      // not just sit in browser history — this app runs three observability agents that
      // attach the current URL to what they send. Analytics puts the full href on every
      // page view, and the error reporters attach the request URL to every report,
      // including the two this screen files when a link fails. The token would be
      // copied to three vendors on a healthcare product.
      //
      // Scrubbed before the first `await` so nothing can fire in between. Only the
      // credential-bearing parameters go; the app's deep link is left alone because
      // later steps still read it. This uses the browser's own history API rather than
      // the router's so the router's location does not change underneath us.
      if (tokenHash || type) {
        params.delete('token_hash');
        params.delete('token');
        params.delete('type');
        const query = params.toString();
        window.history.replaceState(
          window.history.state,
          '',
          `${window.location.pathname}${query ? `?${query}` : ''}`,
        );
      }

      // Supabase reports a dead or tampered link by bouncing back with an error in the
      // URL fragment and no session. Read it first: without this the page would fall
      // through and accept whatever session happened to be left on the device.
      const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      if (fragment.get('error') || fragment.get('error_code')) {
        setStage('expired');
        return;
      }

      try {
        if (tokenHash && type === 'recovery') {
          const { data, error } = await getSupabaseClient().auth.verifyOtp({
            token_hash: tokenHash,
            type: 'recovery',
          });
          if (cancelled) { return; }
          if (data?.session) {
            setStage('ready');
            return;
          }
          // The token did not redeem. Before calling the link dead, check whether a
          // recovery session already exists: these tokens are single-use, and the
          // service that sends the mail may well have spent it on the way here. Telling
          // a member holding a perfectly good session that their link expired sends
          // them round the request-a-link loop for no reason.
          logError('[ResetPassword]', 'verifyOtp did not return a session', error ?? undefined);
        } else {
          // No token in the query: the verify endpoint already consumed it and sent the
          // member here with the session in the fragment. Give the client a moment.
          await new Promise((resolve) => {
            settleTimer = setTimeout(resolve, FRAGMENT_SESSION_SETTLE_MS);
          });
        }

        if (cancelled) { return; }
        const { data: { session } } = await getSupabaseClient().auth.getSession();
        if (cancelled) { return; }
        setStage(session ? 'ready' : 'expired');
      } catch (err) {
        if (cancelled) { return; }
        logError('[ResetPassword]', 'could not establish a session from the link', err);
        setStage('expired');
      }
    };

    establishSession();
    return () => {
      cancelled = true;
      if (settleTimer) { clearTimeout(settleTimer); }
    };
  }, [search]);

  const goToHandoff = () => {
    // Hand the app back its deep link, or the member is stranded.
    //
    // On a phone the app opens the portal inside an in-app browser and adds
    // `redirectTo=sleep-companion://exchange-callback` to the URL first. It then sits
    // watching for the browser to navigate to that scheme carrying a one-time code. If
    // this route drops the value, the member sets their password, everything succeeds,
    // and the app never hears about it — they are left in a browser that goes nowhere,
    // having done everything right.
    //
    // Passing it on to the final step is all that is needed: that step already
    // refreshes the session, asks for the one-time code, and redirects to the deep link
    // with the code attached. Nothing about the token is handled here.
    const redirectParam = buildRedirectParam(effectiveRedirectTo);
    history.replace(`${SLEEPIO_CANOPY_LOGIN_COMPLETE_PATH}${redirectParam}`);
  };

  return (
    <ContentContainer>
      <SleepioHeader />
      <FlexContainer maxWidth="xs">
        {stage === 'checking' && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            style={{ paddingTop: 48 }}
            data-testid="reset-checking"
          >
            <CircularProgress color="primary" style={{ marginBottom: 16 }} />
            <Typography color="textPrimary">
              {t('coverage.pre_onboarding.sleepio.password.reset.checking')}
            </Typography>
          </Box>
        )}

        {stage === 'expired' && (
          <Box data-testid="reset-expired">
            <HeaderBox>
              <Title variant="h2" color="textPrimary">
                {t('coverage.pre_onboarding.sleepio.password.reset.expired_title')}
              </Title>
              <Typography color="textPrimary">
                {t('coverage.pre_onboarding.sleepio.password.reset.expired_body')}
              </Typography>
            </HeaderBox>
            <Button
              variant="contained"
              data-testid="request-new-link-button"
              type="button"
              fullWidth
              color="primary"
              rounded
              style={continueButtonStyles}
              onClick={() => history.push(FORGOT_PASSWORD_PATH)}
            >
              {t('coverage.pre_onboarding.sleepio.password.reset.expired_retry')}
            </Button>
          </Box>
        )}

        {stage === 'ready' && (
          <Box data-testid="reset-choose-password">
            <HeaderBox style={{ marginBottom: 24 }}>
              <Title variant="h2" color="textPrimary">
                {t('coverage.pre_onboarding.sleepio.password.choose.title')}
              </Title>
              <Typography color="textPrimary">
                {t('coverage.pre_onboarding.sleepio.password.choose.sub_title')}
              </Typography>
            </HeaderBox>
            <ChoosePasswordForm onSaved={() => setStage('saved')} />
          </Box>
        )}

        {stage === 'saved' && <PasswordSavedPanel onContinue={goToHandoff} />}
      </FlexContainer>
      <Footer />
    </ContentContainer>
  );
};

const ResetPassword: React.FC = () => (
  <ThemeProvider theme={sleepioTheme}>
    <ResetPasswordInner />
  </ThemeProvider>
);

export default ResetPassword;
